export type ViewerSessionZone =
  { status: 'ready'; timeZone: string } | { status: 'unavailable' };

export type ViewerSessionTime =
  | { status: 'ready'; dateTime: string; shortZone: string; timeZone: string }
  | {
      status: 'unavailable';
      reason: 'invalid session date' | 'timezone error';
      text: string;
    };

export type SessionTimePresentation = 'long' | 'short';

const reported = new Set<string>();
const report = (reason: string) => {
  if (!reported.has(reason)) {
    reported.add(reason);
    console.error(`Viewer session time unavailable: ${reason}`);
  }
};

const validZone = (candidate: unknown): candidate is string => {
  if (typeof candidate !== 'string' || !candidate.trim()) return false;
  try {
    return Boolean(
      new Intl.DateTimeFormat('en-US', {
        timeZone: candidate,
      }).resolvedOptions().timeZone,
    );
  } catch {
    return false;
  }
};

// An explicit candidate lets isolated fixtures exercise invalid-runtime recovery
// without changing Intl globally. Production callers omit it and resolve runtime.
export const resolveViewerSessionZone = (
  runtimeCandidate?: string | null,
): ViewerSessionZone => {
  try {
    const runtimeZone =
      runtimeCandidate === undefined
        ? new Intl.DateTimeFormat().resolvedOptions().timeZone
        : runtimeCandidate;
    if (validZone(runtimeZone))
      return { status: 'ready', timeZone: runtimeZone };
  } catch {
    // Try the browser's default formatter independently.
  }
  try {
    const defaultZone = new Intl.DateTimeFormat('en-US').resolvedOptions()
      .timeZone;
    if (validZone(defaultZone))
      return { status: 'ready', timeZone: defaultZone };
  } catch {
    // No trustworthy zone is available.
  }
  report('timezone error');
  return { status: 'unavailable' };
};

const unavailable = (
  reason: 'invalid session date' | 'timezone error',
): ViewerSessionTime => {
  report(reason);
  return {
    status: 'unavailable',
    reason,
    text: `Time unavailable (${reason})`,
  };
};

// Require an explicit UTC marker or numerical offset; local wall times are ambiguous.
const ABSOLUTE_TIMESTAMP =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-](?:0\d|1[0-4]):[0-5]\d)$/iu;

export const formatViewerSessionTime = (
  instant: unknown,
  zone: ViewerSessionZone,
  presentation: SessionTimePresentation,
): ViewerSessionTime => {
  if (typeof instant !== 'string' || !ABSOLUTE_TIMESTAMP.test(instant))
    return unavailable('invalid session date');
  const date = new Date(instant);
  const [, year, month, day] = /^(\d{4})-(\d{2})-(\d{2})/u.exec(instant) ?? [];
  const calendarDay = new Date(
    Date.UTC(Number(year), Number(month) - 1, Number(day)),
  );
  if (
    Number.isNaN(date.getTime()) ||
    calendarDay.getUTCFullYear() !== Number(year) ||
    calendarDay.getUTCMonth() + 1 !== Number(month) ||
    calendarDay.getUTCDate() !== Number(day)
  )
    return unavailable('invalid session date');
  if (zone.status !== 'ready' || !validZone(zone.timeZone))
    return unavailable('timezone error');

  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      day: 'numeric',
      hour: 'numeric',
      hour12: true,
      minute: '2-digit',
      month: presentation === 'long' ? 'long' : 'short',
      weekday: presentation === 'long' ? 'long' : 'short',
      year: presentation === 'long' ? 'numeric' : undefined,
      timeZone: zone.timeZone,
      timeZoneName: 'short',
    });
    const effectiveZone = formatter.resolvedOptions().timeZone;
    if (
      !validZone(effectiveZone) ||
      effectiveZone !==
        new Intl.DateTimeFormat('en-US', {
          timeZone: zone.timeZone,
        }).resolvedOptions().timeZone
    ) {
      return unavailable('timezone error');
    }
    const values = Object.fromEntries(
      formatter.formatToParts(date).map(({ type, value }) => [type, value]),
    );
    if (
      !values.weekday ||
      !values.month ||
      !values.day ||
      !values.hour ||
      !values.minute ||
      !values.dayPeriod
    )
      return unavailable('timezone error');
    if (presentation === 'long' && !values.year)
      return unavailable('timezone error');
    const dateTime =
      presentation === 'long'
        ? `${values.weekday}, ${values.month} ${values.day}, ${values.year} at ${values.hour}:${values.minute}${values.dayPeriod}`
        : `${values.weekday}, ${values.month} ${values.day} ${values.hour}:${values.minute} ${values.dayPeriod}`;
    return {
      status: 'ready',
      dateTime,
      shortZone: values.timeZoneName || effectiveZone,
      timeZone: effectiveZone,
    };
  } catch {
    return unavailable('timezone error');
  }
};
